import React, {
  ReactNode,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';

import {
  EditorState,
  LexicalEditor,
  SerializedEditorState,
  SerializedLexicalNode,
} from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { SelectionAlwaysOnDisplay } from '@lexical/react/LexicalSelectionAlwaysOnDisplay';
import { HeadingNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { debounce } from 'lodash';
import classNames from 'classnames';

import { Box } from '../../Box';
import { Typography } from '../../Typography';
import { Label } from '../common';

import { ApiUrlContext } from './context';
import { theme } from './themes';
import {
  SendFormPlugin,
  AdditionalFetchDataHelpersPlugin,
  MentionPlugin,
  InitialSelectionPlugin,
} from './plugins';
import { useStyles } from './WysiwygInput.styles';
import {
  CodeBlockNode,
  QuoteNode,
  ReferenceNode,
  FileNode,
  ImagesGalleryNode,
  SingleImageNode,
  VideoNode,
  MentionNode,
  $isMentionNode,
} from './nodes';
import { getAllTextContent, hasNodeType } from './utils';
import { ToolbarPlugins } from './ToolbarPlugins';
import { FunctionalPlugins } from './FunctionalPlugins';

const DEBOUNCE_TIMEOUT = 500;
const MAXIMUM_CHARACTERS_LIMIT = 20000;

export type WysiwygConfigMode = 'default' | 'comments';

export interface WysiwygConfigType {
  plugins: {
    selectAllPlugin: { value: boolean; withDivider: boolean };
    formatHeadingPlugin: {
      heading: boolean;
      fontColor: boolean;
      backgroungColor: boolean;
      withDivider: boolean;
    };
    formatTextPlugin: {
      bold: boolean;
      italic: boolean;
      underline: boolean;
      strikethrough: boolean;
      code: boolean;
      superscript: boolean;
      subscript: boolean;
      withDivider: boolean;
    };
    listAndFormatPlugin: {
      align: boolean;
      list: boolean;
      indent: boolean;
      withDivider: boolean;
    };
    linkAndTablePlugin: {
      link: boolean;
      table: boolean;
      reference: boolean;
      codeBlock: boolean;
      quote: boolean;
      symbols: boolean;
      mention: boolean;
      withDivider: boolean;
    };
    mediaPlugin: {
      singeImage: boolean;
      imagesGallery: boolean;
      video: boolean;
      file: boolean;
      withDivider: boolean;
    };
    undoRedoPlugin: boolean;
    trackMediaIdsAndMentionsPlugin: boolean;
  };
  mode: WysiwygConfigMode;
}

interface WysiwygInputProps {
  config: WysiwygConfigType;
  value: {
    data: SerializedEditorState<SerializedLexicalNode> | undefined;
    hasContent: boolean;
  };
  onChange: (body: {
    data: SerializedEditorState<SerializedLexicalNode>;
    hasContent: boolean;
  }) => void;
  onBlur: () => void;
  onFocus: () => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  tooltip?: ReactNode | null;
  error?: boolean;
  helperText?: string;
  focusMode?: 'auto' | 'manual';
  debounceTimeout?: number;
  maxCharLimit?: number;
  loadFile: (
    file: File,
    type: 'file' | 'image',
  ) => Promise<{
    name: string;
    id: string;
    type: 'image' | 'file';
  }>;
  getAttachmentUrl: (id: string) => Promise<string>;
  searchMentions?: (params: {
    query: string;
    offset: number;
    limit: number;
  }) => Promise<{
    mentions: {
      id: string;
      type: 'user' | 'unit';
      data:
        | {
            id: string;
            first_name: string;
            second_name: string;
            middle_name: string;
            logo?: string;
          }
        | { id: string; name: string };
    }[];
    total: number;
  }>;
  onUploadError: (message: string, title?: string) => void;
  getCurrentMediaIds?: (mediaIds: string[]) => void;
  getCurrentMentionsData?: (
    data: {
      id: string;
      type: string;
    }[],
  ) => void;
  handleSendClick?: () => void;
  checkEditorContainsMention?: (hasMention: boolean) => void;
  onFocusEditor?: (focus: () => void) => void;
  editable?: boolean;
  hideSubmitButton?: boolean;
  apiUrl: string;
  isSubmitting?: boolean;
  showErrors?: boolean;
  initialValue?: SerializedEditorState<SerializedLexicalNode>;
}

const onError = (error: Error) => {
  console.error(error);
};

export const SHARED_EDITOR_CONFIG = {
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    LinkNode,
    TableNode,
    TableRowNode,
    TableCellNode,
    ReferenceNode,
    CodeBlockNode,
    QuoteNode,
    SingleImageNode,
    ImagesGalleryNode,
    VideoNode,
    FileNode,
    MentionNode,
  ],
  theme,
  onError,
};

export const WysiwygInput = ({
  config,
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder = 'Добавьте текст',
  label,
  required = false,
  tooltip = null,
  error = false,
  helperText = '',
  focusMode,
  debounceTimeout = DEBOUNCE_TIMEOUT,
  maxCharLimit = MAXIMUM_CHARACTERS_LIMIT,
  loadFile,
  getAttachmentUrl,
  searchMentions,
  onUploadError,
  getCurrentMediaIds,
  getCurrentMentionsData,
  checkEditorContainsMention,
  handleSendClick,
  onFocusEditor,
  editable = true,
  hideSubmitButton,
  apiUrl,
  isSubmitting = false,
  showErrors,
  initialValue,
}: WysiwygInputProps) => {
  const { classes } = useStyles();
  const [mediaIds, setMediaIds] = useState<string[]>([]);
  const [mentionsData, setMentionsData] = useState<
    { type: string; id: string }[]
  >([]);
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [includeMention, setIncludeMention] = useState(false);

  const editorConfig = useMemo(
    () => ({
      ...SHARED_EDITOR_CONFIG,
      nodes: [
        ...(config.plugins.formatHeadingPlugin.heading ? [HeadingNode] : []),
        ...(config.plugins.linkAndTablePlugin.link ? [LinkNode] : []),
        ...(config.plugins.linkAndTablePlugin.table
          ? [TableNode, TableRowNode, TableCellNode]
          : []),
        ...(config.plugins.linkAndTablePlugin.mention ? [MentionNode] : []),
        ...(config.plugins.linkAndTablePlugin.reference ? [ReferenceNode] : []),
        ...(config.plugins.linkAndTablePlugin.codeBlock ? [CodeBlockNode] : []),
        ...(config.plugins.linkAndTablePlugin.quote ? [QuoteNode] : []),
        ...(config.plugins.listAndFormatPlugin.list
          ? [ListNode, ListItemNode]
          : []),
        ...(config.plugins.mediaPlugin.singeImage ? [SingleImageNode] : []),
        ...(config.plugins.mediaPlugin.imagesGallery
          ? [ImagesGalleryNode]
          : []),
        ...(config.plugins.mediaPlugin.video ? [VideoNode] : []),
        ...(config.plugins.mediaPlugin.file ? [FileNode] : []),
      ],
    }),
    [config.plugins],
  );

  useEffect(() => {
    if (checkEditorContainsMention) {
      checkEditorContainsMention(includeMention);
    }
  }, [includeMention, checkEditorContainsMention]);

  useEffect(() => {
    if (getCurrentMediaIds) {
      getCurrentMediaIds(mediaIds);
    }
  }, [mediaIds, getCurrentMediaIds]);

  useEffect(() => {
    if (getCurrentMentionsData) {
      getCurrentMentionsData(mentionsData);
    }
  }, [mentionsData, getCurrentMentionsData]);

  const initialConfig = useMemo(
    () => ({
      ...editorConfig,
      namespace: 'WysiwigInput',
      editorState: (editor: LexicalEditor) => {
        if (initialValue) {
          editor.setEditorState(editor.parseEditorState(initialValue));

          return;
        }

        if (value?.data) {
          editor.setEditorState(editor.parseEditorState(value.data));
        }
      },
      editable,
    }),
    [editorConfig, value.data, editable, initialValue],
  );

  const debouncedOnChange = useCallback(
    () =>
      debounce((editorState: EditorState) => {
        let textContent = '';
        let hasContent = false;

        editorState.read(() => {
          textContent = getAllTextContent(editorState);
        });

        const lessThenLimit = textContent.length <= maxCharLimit + 1;

        if (lessThenLimit) {
          setIsLimitExceeded(() => false);
          hasContent = Boolean(textContent);
        } else {
          setIsLimitExceeded(() => true);
          hasContent = false;
        }

        if (!hasContent && lessThenLimit) {
          hasContent =
            hasNodeType(editorState, 'file') ||
            hasNodeType(editorState, 'single-image') ||
            hasNodeType(editorState, 'images-gallery') ||
            hasNodeType(editorState, 'video') ||
            hasNodeType(editorState, 'mention-node');
        }

        if (hasNodeType(editorState, 'mention-node', $isMentionNode)) {
          setIncludeMention(() => true);
        } else {
          setIncludeMention(() => false);
        }

        onChange({
          data: editorState.toJSON(),
          hasContent: hasContent,
        });
      }, debounceTimeout),
    [debounceTimeout, onChange, maxCharLimit],
  )();

  return (
    <ApiUrlContext.Provider value={apiUrl}>
      <LexicalComposer initialConfig={initialConfig}>
        <AdditionalFetchDataHelpersPlugin
          getAttachmentUrl={getAttachmentUrl}
          searchMentions={searchMentions}
        >
          <InitialSelectionPlugin />

          <Box
            p="24"
            bgcolor="bg.greyscale.primary"
            radius="l"
            position="relative"
            className={error ? classes.error : ''}
          >
            <ToolbarPlugins
              config={config}
              editable={editable}
              loadFile={loadFile}
              onUploadError={onUploadError}
              classes={classes}
            />
            <FunctionalPlugins
              config={config}
              debouncedOnChange={debouncedOnChange}
              focusMode={focusMode}
              onFocusEditor={onFocusEditor}
              setMediaIds={setMediaIds}
              setMentionsData={setMentionsData}
            />
            <MentionPlugin mention={config.plugins.linkAndTablePlugin.mention}>
              <RichTextPlugin
                contentEditable={
                  <>
                    {label && (
                      <Label
                        label={label}
                        required={required}
                        tooltip={tooltip}
                      />
                    )}
                    <Box
                      display="flex"
                      alignItems="flex-end"
                      flexWrap="nowrap"
                      position="relative"
                    >
                      <Box display="flex" flexDirection="column" width="100%">
                        <ContentEditable
                          id="lexicalContentEditable"
                          className={
                            config.mode === 'comments'
                              ? classNames([
                                  classes.root,
                                  classes.commtensRoot,
                                  error && showErrors && classes.error,
                                  !editable && classes.disabled,
                                ])
                              : classNames([
                                  classes.root,
                                  error && showErrors && classes.error,
                                  !editable && classes.disabled,
                                ])
                          }
                          aria-placeholder={placeholder}
                          placeholder={
                            <Box
                              pointerEvents="none"
                              className={classes.placeholder}
                            >
                              <Typography
                                variant="body2"
                                color="text.light.tertirary"
                              >
                                {placeholder}
                              </Typography>
                            </Box>
                          }
                          onBlur={onBlur}
                          onFocus={onFocus}
                          value={JSON.stringify(value.data)}
                        />
                      </Box>

                      {config.mode === 'comments' && !hideSubmitButton && (
                        <SendFormPlugin
                          hasContent={value.hasContent}
                          handleSendClick={handleSendClick}
                          isSubmitting={isSubmitting}
                        />
                      )}
                    </Box>
                  </>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </MentionPlugin>

            {isLimitExceeded && (
              <Box>
                <Typography variant="caption" color="text.light.secondary">
                  {`Введено максимальное количество символов: ${String(
                    maxCharLimit,
                  ).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`}
                </Typography>
              </Box>
            )}

            {error && helperText && showErrors && (
              <Typography variant="caption" color="accent.text.error">
                {helperText}
              </Typography>
            )}
          </Box>

          <SelectionAlwaysOnDisplay />
        </AdditionalFetchDataHelpersPlugin>
      </LexicalComposer>
    </ApiUrlContext.Provider>
  );
};
